import Service, { type Registry as Services, service } from '@ember/service';
import {
  type OmitEmberDataTypes,
  pushAndReturnPayload,
} from 'frontend-utils/utils/ember-data';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import type { RestrictionAction as RestrictionActionModel } from 'lumen/models';
import type AjaxService from 'lumen/services/ajax';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import type { RestrictionActionPostPayload } from 'lumen/utils/constants/restriction-actions';

export type RestrictionAction = OmitEmberDataTypes<RestrictionActionModel>;

export interface RestrictionOverridePayload {
  context: string;
  from_date: string;
  subscription_id?: number;
  type: string;
  value: number;
}

export default class RepositoriesRestrictionActionService extends Service {
  @service declare private ajax: AjaxService;
  @service declare private store: Services['store'];

  async add(
    restrictionAction: RestrictionActionPostPayload | undefined,
    override?: RestrictionOverridePayload,
  ): Promise<{ apiResponse: RestrictionAction[] | null; isSuccess: boolean }> {
    let apiResponse: RestrictionAction[] | null = null;

    const subscriptionId =
      restrictionAction?.subscription ?? override?.subscription_id;

    const requestUrl = formatUrl(
      `/${APIGATEWAY}/pricing-assistant/restrictions/recommendations/action`,
      { subscription_id: subscriptionId! },
      this.ajax.host,
    );

    const data: {
      override?: RestrictionOverridePayload[];
      restriction_action_configs: RestrictionActionPostPayload[];
    } = {
      restriction_action_configs: restrictionAction ? [restrictionAction] : [],
    };

    if (override) {
      data.override = [override];
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const response = await this.ajax.post(requestUrl, {
      data,
      contentType: 'application/json',
    });

    // SMART DISTRIBUTION TODO: we currently use pushAndReturnPayload because we still use a manual AJAX call here, but we need the normalised record to work well with types
    // Future improvement could be to see if we can get rid of this manual AJAX request and handle things fully with Ember data.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const normalisedResponse = pushAndReturnPayload(this.store, response);
    apiResponse = normalisedResponse[
      'restriction-actions'
    ] as RestrictionAction[];

    return {
      isSuccess: true,
      apiResponse,
    };
  }
}

declare module '@ember/service' {
  interface Registry {
    'repositories/restriction-action': RepositoriesRestrictionActionService;
  }
}
